import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import {
    ActionIcon,
    Box,
    Group,
    Image,
    Loader,
    Stack,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMaximize, IconPhoto, IconTrash, IconUpload } from '@tabler/icons-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';

import { ImageLightbox } from './image-lightbox';

type Props = {
    label?: string;
    description?: string;
    value: string | null | undefined;
    onChange: (url: string | null) => void;
    imageType?: ImageTypeEnum;
    disabled?: boolean;
    /** When false, hide paste-URL input — upload only. Default false for covers. */
    allowUrl?: boolean;
};

const BORDER = 'var(--mantine-color-gray-3)';
const SURFACE = 'var(--mantine-color-gray-0)';
const ACCENT = 'var(--mantine-color-brand-6, var(--mantine-color-blue-6))';

const isImageFile = (file: File) =>
    file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|svg)$/i.test(file.name);

export const CoverUpload = ({
    label,
    description,
    value,
    onChange,
    imageType = ImageTypeEnum.POSTER,
    disabled,
    allowUrl = false,
}: Props) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [over, setOver] = useState(false);
    const [hoverPreview, setHoverPreview] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    // `label=''` hides the field title (section already has one); keep a fallback for a11y.
    const resolvedLabel = label === undefined ? t('common.cover') : label;
    const previewTitle = resolvedLabel || t('common.cover');

    const upload = useCallback(
        async (file: File | null) => {
            if (!file) return;
            if (!isImageFile(file)) {
                notifications.show({
                    color: 'red',
                    message: t('components.coverUpload.dropImageFile'),
                });
                return;
            }

            setUploading(true);
            const data = new FormData();
            data.append('file', file);
            data.append('type', imageType);

            try {
                const { data: res } = await api.uploads.uploadImage({
                    data,
                    onUploadProgress: () => {},
                });
                onChange(res.url);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                setUploading(false);
            }
        },
        [imageType, onChange, t],
    );

    return (
        <Stack gap='xs'>
            {(resolvedLabel || description) && (
                <Stack gap={2}>
                    {resolvedLabel ? (
                        <Text size='sm' fw={500}>
                            {resolvedLabel}
                        </Text>
                    ) : null}
                    {description ? (
                        <Text size='xs' c='dimmed'>
                            {description}
                        </Text>
                    ) : null}
                </Stack>
            )}

            <Box
                onDragEnter={(e) => {
                    e.preventDefault();
                    if (!disabled) setOver(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled) setOver(true);
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    setOver(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    setOver(false);
                    if (disabled || uploading) return;
                    const file =
                        Array.from(e.dataTransfer.files || []).find(isImageFile) || null;
                    void upload(file);
                }}
                style={{
                    position: 'relative',
                    borderRadius: 14,
                    border: `1.5px dashed ${over ? ACCENT : BORDER}`,
                    background: over
                        ? 'color-mix(in srgb, var(--mantine-color-brand-0, #e7f5ff) 80%, white)'
                        : SURFACE,
                    overflow: 'hidden',
                    transition: 'border-color 160ms ease, background 160ms ease',
                }}
            >
                {value ? (
                    <Box style={{ position: 'relative' }}>
                        <UnstyledButton
                            onClick={() => setPreviewOpen(true)}
                            onMouseEnter={() => setHoverPreview(true)}
                            onMouseLeave={() => setHoverPreview(false)}
                            style={{ display: 'block', width: '100%' }}
                            aria-label={t('components.coverUpload.viewFullSize')}
                        >
                            <Image
                                src={value}
                                alt={previewTitle}
                                w='100%'
                                mah={260}
                                fit='cover'
                                style={{ display: 'block', minHeight: 160 }}
                            />
                            <Box
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    paddingBottom: 14,
                                    background:
                                        'linear-gradient(180deg, transparent 45%, rgba(0,0,0,.45))',
                                    opacity: hoverPreview ? 1 : 0,
                                    transition: 'opacity 160ms ease',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Group
                                    gap={6}
                                    px={12}
                                    py={6}
                                    style={{
                                        borderRadius: 999,
                                        background: 'rgba(0,0,0,.55)',
                                        color: 'white',
                                    }}
                                >
                                    <IconMaximize size={14} />
                                    <Text size='xs' fw={600} c='white'>
                                        {t('components.coverUpload.viewFullSize')}
                                    </Text>
                                </Group>
                            </Box>
                        </UnstyledButton>

                        {!disabled && (
                            <Group
                                gap={4}
                                style={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    zIndex: 1,
                                }}
                            >
                                <Tooltip label={t('components.coverUpload.replace')}>
                                    <ActionIcon
                                        variant='white'
                                        color='dark'
                                        radius='xl'
                                        size='md'
                                        disabled={uploading}
                                        onClick={() => inputRef.current?.click()}
                                        aria-label={t('components.coverUpload.replace')}
                                    >
                                        {uploading ? (
                                            <Loader size={14} />
                                        ) : (
                                            <IconUpload size={16} />
                                        )}
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label={t('components.coverUpload.remove')}>
                                    <ActionIcon
                                        variant='white'
                                        color='red'
                                        radius='xl'
                                        size='md'
                                        disabled={uploading}
                                        onClick={() => onChange(null)}
                                        aria-label={t('components.coverUpload.remove')}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        )}
                    </Box>
                ) : (
                    <UnstyledButton
                        disabled={disabled || uploading}
                        onClick={() => inputRef.current?.click()}
                        style={{ width: '100%' }}
                    >
                        <Stack align='center' justify='center' gap={6} py={40} px='md'>
                            {uploading ? (
                                <Loader size={22} />
                            ) : (
                                <Box
                                    style={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 12,
                                        display: 'grid',
                                        placeItems: 'center',
                                        background: 'var(--mantine-color-white)',
                                        border: `1px solid ${BORDER}`,
                                    }}
                                >
                                    <IconPhoto size={20} stroke={1.5} color={ACCENT} />
                                </Box>
                            )}
                            <Text size='sm' fw={600}>
                                {uploading
                                    ? t('components.coverUpload.uploading')
                                    : t('components.coverUpload.dropHere')}
                            </Text>
                            <Text size='xs' c='dimmed'>
                                {t('components.coverUpload.orClickToBrowse')}
                            </Text>
                        </Stack>
                    </UnstyledButton>
                )}
            </Box>

            {allowUrl ? (
                <TextInput
                    size='sm'
                    placeholder={t('components.coverUpload.orPasteUrl')}
                    value={value || ''}
                    disabled={disabled || uploading}
                    onChange={(e) => onChange(e.currentTarget.value.trim() || null)}
                />
            ) : null}

            <input
                ref={inputRef}
                type='file'
                accept='image/*'
                hidden
                disabled={disabled || uploading}
                onChange={(e) => {
                    const file = e.currentTarget.files?.[0] || null;
                    e.currentTarget.value = '';
                    void upload(file);
                }}
            />

            {value ? (
                <ImageLightbox
                    urls={[value]}
                    index={0}
                    opened={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    title={previewTitle}
                />
            ) : null}
        </Stack>
    );
};
